import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RiAddLine,
  RiEdit2Line,
  RiDeleteBin6Line,
  RiSearchLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";

import {
  getRawMaterials,
  deleteRawMaterial,
} from "../services/rawMaterialService";

const RawMaterialListLayer = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔍 search
  const [search, setSearch] = useState("");

  // 📄 pagination (UI only)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    loadMaterials();
  }, []);

  /* LOAD MATERIALS (UNCHANGED) */
  const loadMaterials = async () => {
    try {
      setLoading(true);
      const res = await getRawMaterials();

      if (res?.status === "success") {
        setMaterials(res.data || []);
      } else {
        toast.error(res?.message || "Failed to load materials");
      }
    } catch (err) {
      console.error(err);
      toast.error("API Error");
    }
    setLoading(false);
  };

  /* DELETE (UNCHANGED) */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this raw material?")) return;

    try {
      const res = await deleteRawMaterial(id);
      if (res?.status === "success") {
        toast.success("Raw material deleted");
        loadMaterials();
      } else {
        toast.error(res?.message);
      }
    } catch {
      toast.error("API Error");
    }
  };

  /* 🔍 SEARCH (UI only) */
  const filteredData = useMemo(() => {
    return materials.filter((m) =>
      `${m.name} ${m.code}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [materials, search]);

  /* 📄 PAGINATION (UI only) */
  const effectivePageSize =
    pageSize === "all" ? filteredData.length : pageSize;

  const totalPages =
    effectivePageSize === 0
      ? 1
      : Math.ceil(filteredData.length / effectivePageSize);

  const paginatedData =
    pageSize === "all"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * effectivePageSize,
          currentPage * effectivePageSize
        );

  return (
    <div className="ul-page">
      {/* HEADER */}
      <div className="ul-header">
        <h4>Raw Material List</h4>
        <Link to="/add-raw-material" className="ul-add-btn">
          <RiAddLine /> Add Raw Material
        </Link>
      </div>

      {/* FILTER BAR */}
      <div className="ul-filters">
        <div className="ul-search">
          <RiSearchLine />
          <input
            placeholder="Search raw material..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="ul-pagesize">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(
                e.target.value === "all"
                  ? "all"
                  : Number(e.target.value)
              );
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value="all">All</option>
          </select>
          <span>entries</span>
        </div>
      </div>

      {/* TABLE */}
      <div className="ul-table-wrapper">
        <table className="ul-table">
          <thead>
            <tr>
              <th>#</th>
              {/* <th>Code</th> */}
              <th>Name</th>
              <th>Rate</th>
              <th>Stock</th>
              {/* <th>Status</th> */}
              <th className="text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  No raw materials found
                </td>
              </tr>
            ) : (
              paginatedData.map((m, i) => (
                <tr key={m.id}>
                  <td>
                    {pageSize === "all"
                      ? i + 1
                      : (currentPage - 1) * effectivePageSize + i + 1}
                  </td>
                  {/* <td>{m.code}</td> */}
                  <td>{m.name}</td>
                  <td>₹{m.rate_per_unit}</td>
                  <td>{m.opening_stock}</td>
                  {/* <td>
                    <span
                      className={`badge ${
                        m.del_status === "Live"
                          ? "bg-success"
                          : "bg-danger"
                      }`}
                    >
                      {m.del_status}
                    </span>
                  </td> */}
                  <td className="ul-actions">
                    <Link
                      to={`/edit-raw-material/${m.id}`}
                      className="ul-btn edit"
                    >
                      <RiEdit2Line /> Edit
                    </Link>

                    <button
                      className="ul-btn delete"
                      onClick={() => handleDelete(m.id)}
                    >
                      <RiDeleteBin6Line /> Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {pageSize !== "all" && totalPages > 1 && (
        <div className="ul-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <RiArrowLeftSLine />
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <RiArrowRightSLine />
          </button>
        </div>
      )}
    </div>
  );
};

export default RawMaterialListLayer;
